import React, { FC, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';

import CircularProgress from '@mui/material/CircularProgress';

import { useRollbarPerson } from '@rollbar/react';
import { analytics } from 'analytics';
import useCurrentUser from 'hooks/useCurrentUser';
import { sessionExpired, setPermissions, signOutUser, updateAccessToken } from 'redux/slices/user.slice';
import baseApi from 'services/api/baseApi';
import { useGetProjectQuery } from 'services/api/projects.api';
import { useGetUserPermissionsQuery, useRefreshSessionMutation } from 'services/api/user.api';
import { revokeFcmOnForcedSignOut } from 'services/fcmTokenStore';
import { withRefreshLock } from 'services/session/refreshLock';
import { adopted } from 'services/session/sessionMiddleware';

import { checkIfTokenIsValid } from '../authStorage';
import { canAccessWithAdminBypass, isCountryIncluded } from '../utils/permissions';
import routes, { Route as RouteType } from './routes';

interface Props {
  route: RouteType;
}

const AuthRoute: FC<Props> = ({ route }) => {
  const { component: Component, path, private: privateRoute, permissions: requiredPermissions, adminOnly } = route;
  const user = useCurrentUser();
  const location = useLocation();
  const dispatch = useDispatch();
  const [refreshSession, { isLoading: isRefreshingSession }] = useRefreshSessionMutation();
  const recoveryAttemptedForToken = useRef<string | null>(null);
  const hasStoredSession = Boolean(user?.accessToken);
  const userIsValid = checkIfTokenIsValid(user);
  const needsSessionRecovery = hasStoredSession && !userIsValid;
  const shouldFetchPermissions = hasStoredSession && userIsValid && privateRoute;

  const {
    data: fetchedPermissions,
    currentData: currentUserPermissions,
    isFetching: isQueryFetching,
  } = useGetUserPermissionsQuery(user?.id ?? null, {
    skip: !shouldFetchPermissions,
  });
  const isLoadingPermissions = isQueryFetching && !currentUserPermissions;

  const denyRedirectTo = useMemo(() => {
    if (!requiredPermissions?.redirectOnDeny) {
      return '/';
    }
    return requiredPermissions.redirectOnDeny(location.pathname, location.search);
  }, [location.pathname, location.search, requiredPermissions]);

  useEffect(() => {
    if (!needsSessionRecovery || !user?.accessToken || recoveryAttemptedForToken.current === user.accessToken) {
      return;
    }

    recoveryAttemptedForToken.current = user.accessToken;
    withRefreshLock(user.accessToken, () =>
      refreshSession()
        .unwrap()
        .then(({ accessToken }) => accessToken)
        .catch((): null => null)
    )
      .then((accessToken) => {
        if (!accessToken) throw new Error('Session recovery failed');
        dispatch(adopted(updateAccessToken(accessToken)));
      })
      .catch(() => {
        dispatch(sessionExpired('refresh_failed'));
        revokeFcmOnForcedSignOut().catch(() => {});
        dispatch(baseApi.util.resetApiState());
        dispatch(signOutUser());
      });
  }, [dispatch, needsSessionRecovery, refreshSession, user?.accessToken]);

  useEffect(() => {
    if (shouldFetchPermissions && fetchedPermissions) {
      // Update the permissions and hash in the redux store
      dispatch(setPermissions(fetchedPermissions));
    }
  }, [fetchedPermissions, dispatch, shouldFetchPermissions]);

  // Identify user in PostHog for analytics tracking
  useEffect(() => {
    if (user?.id && userIsValid) {
      analytics.identify(user.id, {
        email: user.email,
        projectId: user.projectId,
        isAdmin: user.isAdmin,
        permissions: user.permissions,
        countryCode: user.countryCode,
      });
    }
  }, [user?.id, user?.email, user?.projectId, user?.isAdmin, user?.role, userIsValid]);

  // Cached query most screens already make; its details label the project group identify created.
  const { data: project } = useGetProjectQuery(null, { skip: !shouldFetchPermissions || !user?.projectId });
  useEffect(() => {
    if (!project) return;
    analytics.setProjectGroupProperties(project.id, { name: project.name, countryCode: project.countryCode });
  }, [project]);

  if (user) {
    useRollbarPerson({
      id: user.id,
      email: user.email,
      projectId: user.projectId,
      isAdmin: user.isAdmin,
    });
  }

  const getValidRedirectUrl = (redirectUrl: string) => {
    const redirectUrlPath = redirectUrl.split('?')[0];
    const validRedirectUrl = Object.values(routes).some(
      (routeValue) => routeValue.path === redirectUrlPath || routeValue.regex?.test(redirectUrlPath)
    );
    return validRedirectUrl ? redirectUrl : '/';
  };

  if (userIsValid && (path === '/sign-in' || path === '/sign-up' || path === '/forgot-password')) {
    // Adds the text after '?redirect=/'
    const redirectUrl = `/${window.location.search.slice(11)}`;
    const validRedirect = getValidRedirectUrl(redirectUrl);
    // After sign-in, default to client-management unless a specific page was requested
    const destination = validRedirect === '/' ? '/client-management' : validRedirect;
    return <Navigate to={destination} />;
  }

  if (needsSessionRecovery || isRefreshingSession) {
    return <CircularProgress size={40} sx={{ display: 'block', margin: 'auto', marginTop: '20vh' }} />;
  }

  if (privateRoute && !hasStoredSession) {
    const signInPath = `/sign-in?redirect=${window.location.pathname}${window.location.search}`;
    return <Navigate to={signInPath} />;
  }

  if (privateRoute && adminOnly && !user?.isAdmin) {
    return <Navigate to="/" />;
  }

  if (
    privateRoute &&
    userIsValid &&
    requiredPermissions &&
    !isCountryIncluded(user?.countryCode, requiredPermissions.includedCountryCodes)
  ) {
    return <Navigate to="/" />;
  }

  if (privateRoute && userIsValid && requiredPermissions) {
    if (isLoadingPermissions) {
      return <CircularProgress size={40} sx={{ display: 'block', margin: 'auto', marginTop: '20vh' }} />;
    }

    const isAdmin = !!user?.isAdmin;
    const hasDeny = !!requiredPermissions.deny?.length;
    const currentPermissions = fetchedPermissions?.permissions || user?.permissions;
    const needsPermissions = !isAdmin || hasDeny;

    if (needsPermissions && (!currentPermissions || currentPermissions.length === 0)) {
      return <CircularProgress size={40} sx={{ display: 'block', margin: 'auto', marginTop: '20vh' }} />;
    }

    const allowed = canAccessWithAdminBypass(
      { isAdmin, userPermissions: currentPermissions, countryCode: user?.countryCode },
      requiredPermissions
    );

    if (!allowed) {
      return <Navigate to={denyRedirectTo} />;
    }
  }

  return <Component />;
};

export default AuthRoute;
